import React, { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import { Award, Cake, CalendarDays } from "lucide-react";
import clsx from "clsx";
import styles from "./celebration-banners.module.css";
import { useCelebrations } from "../../../../../hooks/use-celebrations.ts";
import type { DashboardTrainingSession } from "../../../../../hooks/use-training-sessions.ts";
import { getCelebrationCardTitle } from "../../utils/celebration-banner-titles.ts";
import { Avatar } from "../../../../ui/avatar/avatar.tsx";
import { useProfileImage } from "../../../../../hooks/use-profile-image.tsx";
import {
  getSessionBannerHeadline,
  getSessionsForCelebrationBanner,
} from "../../utils/training-session-banner.ts";
import { SessionBannerCard } from "./session-banner-card.tsx";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

interface CelebrationItemProps {
  id: number;
  name: string;
  title: string;
  date?: string | null;
  type: "BIRTHDAY" | "ANNIVERSARY";
}

function formatCelebrationDate(value?: string | null): string | null {
  const calendarDate = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  if (!calendarDate) return null;
  const [, year, month, day] = calendarDate;
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCMonth() + 1 !== Number(month) ||
    date.getUTCDate() !== Number(day)
  ) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "long", day: "numeric", timeZone: "UTC",
  }).format(date);
}

function CelebrationSlide({ id, name, title, type, date }: CelebrationItemProps) {
  const profileImage = useProfileImage(id);
  const dateLabel = formatCelebrationDate(date);

  return (
    <article
      className={clsx(
        styles.card,
        type === "BIRTHDAY" ? styles.cardBirthday : styles.cardAnniversary,
      )}
    >
      <div className={styles.leftContent}>
        <div className={styles.iconWrap}>
          {type === "BIRTHDAY" ? (
            <Cake className={styles.icon} aria-hidden />
          ) : (
            <Award className={styles.icon} aria-hidden />
          )}
        </div>
        <div className={styles.textBlock}>
          <p
            className={clsx(
              "heading-xs mb-0",
              type === "BIRTHDAY"
                ? styles.headlineBirthday
                : styles.headlineAnniversary,
            )}
          >
            {title}
          </p>
          <p
            className={clsx(
              "sub-heading-xs mb-0",
              type === "BIRTHDAY"
                ? styles.sublineBirthday
                : styles.sublineAnniversary,
            )}
          >
            {name}
          </p>
          {dateLabel && (
            <p className={clsx(
              styles.date,
              type === "BIRTHDAY" ? styles.sublineBirthday : styles.sublineAnniversary,
            )}>
              <CalendarDays size={12} aria-hidden />
              <span>{dateLabel}</span>
            </p>
          )}
        </div>
      </div>
      <Avatar
        name={name}
        src={profileImage || "/assets/profile.png"}
        size="md"
        className={styles.avatar}
      />
    </article>
  );
}

export type CelebrationBannersProps = {
  trainingSessions?: DashboardTrainingSession[];
};

export function CelebrationBanners({
  trainingSessions,
}: CelebrationBannersProps) {
  const { data } = useCelebrations();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const { birthdays, anniversaries } = useMemo(() => {
    if (!data) return { birthdays: [], anniversaries: [] };
    return {
      birthdays: data.filter((d) => d.type === "BIRTHDAY"),
      anniversaries: data.filter((d) => d.type === "ANNIVERSARY"),
    };
  }, [data]);

  const { slides: bannerSlides } = useMemo(
    () => getSessionsForCelebrationBanner(trainingSessions, now),
    [trainingSessions, now],
  );

  const getCarouselSettings = (count: number) => ({
    dots: count > 1,
    infinite: count > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: count > 1,
    autoplaySpeed: 3000,
    cssEase: "linear",
    pauseOnHover: true,
  });

  const hasBirthdays = birthdays.length > 0;
  const hasAnniversaries = anniversaries.length > 0;
  const hasSessions = bannerSlides.length > 0;
  const columnCount =
    (hasBirthdays ? 1 : 0) + (hasAnniversaries ? 1 : 0) + (hasSessions ? 1 : 0);

  if (!hasBirthdays && !hasAnniversaries && !hasSessions) return null;

  return (
    <section
      className={clsx(
        styles.grid,
        columnCount === 1 && styles.gridCols1,
        columnCount === 2 && styles.gridCols2,
        columnCount === 3 && styles.gridCols3,
      )}
      aria-label="Team celebrations"
    >
      {hasBirthdays && (
        <div className={styles.carouselSection}>
          <Slider {...getCarouselSettings(birthdays.length)}>
            {birthdays.map((b, index) => (
              <div
                key={`birthday-${b.bucket}-${b.id}-${index}`}
                className={styles.carouselContainer}
              >
                <CelebrationSlide
                  id={b.id}
                  name={b.name}
                  date={b.birthdayDate}
                  title={getCelebrationCardTitle(b)}
                  type="BIRTHDAY"
                />
              </div>
            ))}
          </Slider>
        </div>
      )}

      {hasAnniversaries && (
        <div className={styles.carouselSection}>
          <Slider {...getCarouselSettings(anniversaries.length)}>
            {anniversaries.map((a, index) => (
              <div
                key={`anniversary-${a.bucket}-${a.id}-${index}`}
                className={styles.carouselContainer}
              >
                <CelebrationSlide
                  id={a.id}
                  name={a.name}
                  date={a.anniversaryDate}
                  title={getCelebrationCardTitle(a)}
                  type="ANNIVERSARY"
                />
              </div>
            ))}
          </Slider>
        </div>
      )}

      {hasSessions && (
        <div className={styles.carouselSection}>
          <Slider {...getCarouselSettings(bannerSlides.length)}>
            {bannerSlides.map(({ session, isUpcoming }) => (
              <div key={session.id} className={styles.carouselContainer}>
                <SessionBannerCard
                  session={session}
                  headline={getSessionBannerHeadline(session, now, isUpcoming)}
                />
              </div>
            ))}
          </Slider>
        </div>
      )}
    </section>
  );
}
